import type { DefaultSuperInsuranceOutput } from "./types.js";

// Plain-English result copy for the default-super-insurance awareness checker.
// Centralised in the rules package so every surface (Expo result screen, "my
// results", PDF report) renders identical wording from one source — which
// matters for REGULATION, not just DRY.
//
// Every string here is INFORMATION, not advice (validation §3; panel §2):
//   * It states WHICH default-cover rule applies to an account like the one
//     described and how that rule works — never a finding about THIS account.
//   * NO personalised dollar / premium / coverage number (there is none).
//   * NO super-fund / super-product handoff (default cover is s764A).
//   * NEVER "keep / cancel / buy / switch" — the keep-vs-cancel decision is the
//     advice line for a non-carved-out financial product. We mirror ASIC
//     MoneySmart's NEUTRAL "check before changing or closing a fund / premiums
//     reduce your balance" cautions instead.
//   * The duplicate-premium hook is strictly TYPICAL-CASE + CONDITIONAL +
//     SOURCED (misleading-conduct hygiene, panel build-condition 3).
// Drift from information → advice is the kill-threat, so the copy lives beside
// the rules it describes. Pure string-builders, unit-tested.

const DISCLAIMER = "Educational tool. Not financial advice.";

/** Headline: states WHICH way the rules point for an account like this — no
 * number, no steer. */
export function headline(out: DefaultSuperInsuranceOutput): string {
  if (out.defaultCoverLikelyOff) {
    return "On the rules alone, an account like this would generally have default insurance switched off — unless you have opted in";
  }
  if (out.defaultCoverGenerallyApplies) {
    return "On the rules alone, an account like this would generally have default insurance switched on";
  }
  return "Whether default insurance applies to an account like this depends on the rules below";
}

/** Explains the under-25 PMIF rule when it applies. */
export function under25Bullet(out: DefaultSuperInsuranceOutput): string | null {
  if (!out.under25) return null;
  return "Under the Putting Members' Interests First rules, super funds do not provide insurance automatically to new members under 25 — it is provided on an opt-out basis only once you turn 25, unless you have elected to take it earlier.";
}

/** Explains the $6,000 PMIF rule when it applies. */
export function balanceUnder6kBullet(
  out: DefaultSuperInsuranceOutput,
): string | null {
  if (!out.balanceUnder6k) return null;
  return "Under the Putting Members' Interests First rules, super funds do not provide insurance automatically while an account balance has not reached A$6,000 — cover is provided on an opt-out basis once the balance reaches A$6,000, unless you have elected to take it earlier.";
}

/** Explains the 16-month PYS inactivity rule. Stated as the rule even when the
 * trigger isn't asserted (inactivity unknown), but never asserts it about this
 * account. */
export function inactivityBullet(
  out: DefaultSuperInsuranceOutput,
): string | null {
  if (out.inactive16Months === true) {
    return "Under the Protecting Your Super rules, a fund must switch off insurance on an account that has not received a contribution or rollover for 16 months, unless the member has elected to keep it. An account inactive for that long would generally have its insurance switched off.";
  }
  if (out.inactive16Months === false) {
    return "Under the Protecting Your Super rules, a fund switches off insurance on an account inactive for 16 months unless the member elects to keep it. On the inactivity entered, that 16-month rule would not yet apply to an account like this.";
  }
  // Inactivity unknown — explain the rule without asserting it.
  return "Under the Protecting Your Super rules, a fund switches off insurance on an account that has not received a contribution or rollover for 16 months, unless the member elects to keep it. You can check the last activity date on your statement or in myGov.";
}

/**
 * The duplicate-premium wedge — STRICTLY typical-case + conditional + sourced.
 * Only shown when the user reported 2+ accounts, and even then phrased as "may"
 * about a typical case, never an assertion that THIS user is paying twice.
 */
export function duplicateAccountsBullet(
  out: DefaultSuperInsuranceOutput,
): string | null {
  if (!out.multipleAccounts) return null;
  return "If you hold more than one active super account, each account that has default insurance attached is generally charging its own premiums, which reduce that account's balance. People with multiple accounts may be paying for cover more than once. ASIC's MoneySmart notes that consolidating super can stop duplicate premiums — but says to check you are not cancelling cover you would want to keep before changing or closing a fund.";
}

/** Mirrors MoneySmart's neutral standing caution: premiums reduce your balance;
 * check cover before changing/closing a fund. Never a keep/cancel steer. */
export function cautionBullet(): string {
  return "Insurance premiums inside super are deducted from your account balance. Before changing or closing a super account, MoneySmart suggests checking what insurance you have and whether you would still be able to get equivalent cover — some cover can be harder to obtain later.";
}

/** Where to see your own real position — a government action, not a product. */
export function whereToCheckBullet(): string {
  return "You can see your super accounts, balances and whether insurance is attached through myGov (linked to the ATO), or on each fund's statement. Consolidating super is done through myGov — a free government service, not a product.";
}

/** Source-attribution + the precise 2019/2020 framing the ledger requires. */
export function sourceBullet(fy: string, verifiedAt: string): string {
  return `These rules come from the 2019 super reforms — Protecting Your Super and Putting Members' Interests First (PMIF operative from 1 April 2020) — as explained by APRA and ASIC's MoneySmart (ruleset ${fy}, last verified ${verifiedAt}). This tool explains how the rules apply to an account like the one described; it does not tell you what your own account holds — check your fund or myGov. ${DISCLAIMER}`;
}

/** Compose the full explanatory bullet list for a result. */
export function explanationBullets(
  out: DefaultSuperInsuranceOutput,
  fy: string,
  verifiedAt: string,
): string[] {
  return [
    under25Bullet(out),
    balanceUnder6kBullet(out),
    inactivityBullet(out),
    duplicateAccountsBullet(out),
    cautionBullet(),
    whereToCheckBullet(),
    sourceBullet(fy, verifiedAt),
  ].filter((s): s is string => s !== null);
}
